import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import NotesSection from '../NotesSection/NotesSection';
import Styles from './Styles.module.css';

const RightPanel = ({ open, activeTab, onTabChange, roomId, currentUserId, peers, peerNames = [], currentUser, sharedFiles = [], onShareFile, chatMessages = [], onSendChatMessage }) => {
  const { t } = useTranslation();
  const [fileUrl, setFileUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [shareError, setShareError] = useState('');
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef(null);
  const tabs = [
    { id: 'chat', label: t('screens.video.group.tabChat') },
    { id: 'people', label: t('screens.video.group.tabPeople') },
    { id: 'notes', label: t('screens.video.group.tabNotes') },
    { id: 'files', label: t('screens.video.group.tabFiles') },
  ];

  const participantCount = (peers?.length || 0) + 1;

  useEffect(() => {
    if (activeTab === 'chat') chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, activeTab]);

  const handleChatSend = () => {
    if (!chatInput.trim()) return;
    onSendChatMessage(chatInput);
    setChatInput('');
  };

  const handleSubmitFile = () => {
    const trimUrl = fileUrl.trim();
    const trimName = fileName.trim();
    if (!trimUrl) { setShareError(t('screens.video.group.fileUrlError')); return; }
    setShareError('');
    onShareFile({ name: trimName || trimUrl, url: trimUrl });
    setFileUrl('');
    setFileName('');
  };

  const fileExchangeUrl = `${window.location.origin}${window.location.pathname}#/secure-files`;

  return (
    <div className={`${Styles.panel} ${open ? Styles.open : ''}`}>
      {/* Tab bar */}
      <div className={Styles.panelTabs}>
        {tabs.map(t => (
          <button
            key={t.id}
            className={`${Styles.ptab} ${activeTab === t.id ? Styles.ptabActive : ''}`}
            onClick={() => onTabChange(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Panel body */}
      <div className={Styles.panelBody}>

        {/* Chat */}
        {activeTab === 'chat' && (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {chatMessages.length === 0 ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', textAlign: 'center', gap: 8 }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                  </svg>
                  <span style={{ fontSize: 12 }}>{t('screens.video.group.noMessages')}</span>
                </div>
              ) : (
                chatMessages.map((msg, i) => {
                  const isLocal = msg.isLocal || msg.senderId === currentUserId;
                  return (
                    <div key={i} style={{ display: 'flex', flexDirection: isLocal ? 'row-reverse' : 'row', gap: 7, alignItems: 'flex-end' }}>
                      <div style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0, background: isLocal ? '#800020' : '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff' }}>
                        {(msg.senderName || 'U')[0].toUpperCase()}
                      </div>
                      <div style={{ maxWidth: '75%' }}>
                        {!isLocal && <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 3, fontWeight: 600 }}>{msg.senderName}</div>}
                        <div style={{ background: isLocal ? '#800020' : '#1e293b', color: '#f1f5f9', borderRadius: isLocal ? '10px 10px 2px 10px' : '10px 10px 10px 2px', padding: '7px 11px', fontSize: 13, lineHeight: 1.45, wordBreak: 'break-word' }}>
                          {msg.text}
                        </div>
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 3, textAlign: isLocal ? 'right' : 'left' }}>
                          {new Date(msg.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>
            {/* Input */}
            <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(148,163,184,0.15)', display: 'flex', gap: 8, flexShrink: 0 }}>
              <input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
                placeholder={t('screens.video.group.messageEveryone')}
                style={{ flex: 1, background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8, padding: '8px 11px', fontSize: 13, color: '#f1f5f9', outline: 'none', fontFamily: 'inherit' }}
              />
              <button
                onClick={handleChatSend}
                style={{ width: 36, height: 36, borderRadius: 8, background: '#800020', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* People */}
        {activeTab === 'people' && (
          <div className={Styles.peopleTab}>
            <div className={Styles.peopleHeader}>
              {t('screens.video.group.participantCount', { count: participantCount })}
            </div>
            {/* Local user */}
            <div className={Styles.personRow}>
              <div className={`${Styles.personAv} ${Styles.burgAv}`}>
                {currentUser?.name ? currentUser.name[0].toUpperCase() : 'Y'}
              </div>
              <div className={Styles.personInfo}>
                <div className={Styles.personName}>
                  {currentUser?.name || t('screens.video.group.youFallback')}
                  <span className={Styles.youTag}>{t('screens.video.group.youPersonSuffix')}</span>
                </div>
              </div>
            </div>
            {/* Remote peers */}
            {(peers || []).map((_, i) => {
              const name = peerNames[i] || t('screens.video.group.participant', { index: i + 1 });
              const initial = name[0]?.toUpperCase() || 'P';
              return (
                <div key={i} className={Styles.personRow}>
                  <div className={`${Styles.personAv} ${Styles.greenAv}`}>
                    {initial}
                  </div>
                  <div className={Styles.personInfo}>
                    <div className={Styles.personName}>{name}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Notes — reuse existing NotesSection */}
        {activeTab === 'notes' && (
          <div style={{ overflow: 'hidden', width: '100%' }}>
            <NotesSection
              showNotes={true}
              setShowNotes={() => {}}
              roomId={roomId}
              currentUserId={currentUserId}
            />
          </div>
        )}

        {/* Files */}
        {activeTab === 'files' && (
          <div className={Styles.filesTab}>
            {/* Open File Exchange */}
            <a
              href={fileExchangeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={Styles.fileExchangeBtn}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              {t('screens.video.group.openFileExchange')}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>

            <p className={Styles.fileHint}>
              {t('screens.video.group.fileHint')}
            </p>

            {/* Share input */}
            <div className={Styles.fileShareForm}>
              <input
                className={Styles.fileInput}
                type="text"
                placeholder={t('screens.video.group.fileUrlPh')}
                value={fileUrl}
                onChange={e => setFileUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmitFile()}
              />
              <input
                className={Styles.fileInput}
                type="text"
                placeholder={t('screens.video.group.fileNamePh')}
                value={fileName}
                onChange={e => setFileName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmitFile()}
              />
              {shareError && <div className={Styles.fileError}>{shareError}</div>}
              <button className={Styles.fileShareBtn} onClick={handleSubmitFile}>
                {t('screens.video.group.shareWithParticipants')}
              </button>
            </div>

            {/* Shared files list */}
            {sharedFiles.length === 0 ? (
              <div className={Styles.noFiles}>{t('screens.video.group.noFilesShared')}</div>
            ) : (
              <div className={Styles.filesList}>
                {sharedFiles.map((f, i) => (
                  <div key={i} className={Styles.fileRow}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" style={{ flexShrink: 0 }}>
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <div className={Styles.fileRowInfo}>
                      <a href={f.url} target="_blank" rel="noopener noreferrer" className={Styles.fileLink}>
                        {f.name}
                      </a>
                      <span className={Styles.fileSharedBy}>{t('screens.video.group.sharedBy', { name: f.sharedBy })}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RightPanel;
